import dayjs from 'dayjs';
import {
  buildStatusMetricCards,
  formatRelatedEntityCountLabel,
  type MetricCardItem,
  type MetricCardOption,
} from '@/features/metrics/components/statusMetricCards';
import { normaliseMetricsToolbarTagOptions } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import {
  buildCountedDropdownOptions,
  buildStringArrayCountMap,
  buildStringCountMap,
} from '@/features/shared/components/tableSystem/filterOptionCounts';
import {
  buildMetricChartSeriesFromBuckets,
  buildMetricSummaryFromBuckets,
  createDailyExecutionMetricBucket,
} from '@/features/shared/utils/chartBucketSeries';
import { resolveChartDates } from '@/features/shared/utils/chartDateWindow';
import { buildStatusOptions } from '@/shared/constants/statusOptions';
import type { EntityDailyHistoryRow } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel';
import type { DailyHistorySortKey } from '@/features/shared/components/entityDailyHistory/EntityDailyHistoryPanel.types';
import type { MetricsToolbarTagOption } from '@/features/shared/components/metricsToolbar/metricsToolbarTagOptions';
import type { TableDropdownOption } from '@/features/shared/components/tableSystem/tableToolbar.types';
import type { UiTone } from '@/theme/uiTokens';
import type { BuildDto, BuildStatus, JobListItemDto } from '@flowqa/shared';

export type JobRunMetricsSummary = {
  passRateChartData: Array<Record<string, number | string | null>>;
  frequencyChartData: Array<Record<string, number | string | null>>;
  durationChartData: Array<Record<string, number | string | null>>;
  flakeScoreChartData: Array<Record<string, number | string | null>>;
  passRate: number | null;
  frequencyPerDay: number | null;
  avgDurationSeconds: number | null;
  flakeScore: number | null;
};

export type JobRunMetricBucket = ReturnType<typeof createDailyExecutionMetricBucket>;

export const buildJobStatusSeries = (
  visibleStatuses: BuildStatus[],
): Array<{ key: BuildStatus; label: string; colorKey: UiTone }> =>
  visibleStatuses.map((status) => ({
    key: status,
    label: buildStatusOptions.find((option) => option.value === status)?.label ?? status,
    colorKey:
      status === 'completed'
        ? 'green'
        : status === 'failed'
          ? 'red'
          : status === 'running'
            ? 'blue'
            : 'neutral',
  }));

export const resolveVisibleBuildStatuses = (selectedStatuses: BuildStatus[]): BuildStatus[] =>
  selectedStatuses.length > 0 ? selectedStatuses : buildStatusOptions.map((option) => option.value);

export const resolveLatestBuildDay = (runs: BuildDto[]): string | null => {
  if (runs.length === 0) return null;
  const latestTimestamp = runs
    .map(resolveBuildTimestamp)
    .sort((left, right) => right.getTime() - left.getTime())[0];
  return latestTimestamp.toISOString().slice(0, 10);
};

export const serialiseSelectedIds = (ids: string[]): string =>
  [...ids].sort((left, right) => left.localeCompare(right)).join(',');

export const formatSelectedDateRangeLabel = (
  startDay: string | null,
  endDay: string | null,
): string | null =>
  startDay && endDay
    ? `${dayjs(startDay).format('MMM D, YYYY')} - ${dayjs(endDay).format('MMM D, YYYY')}`
    : null;

export const buildJobStatusCountsByDate = (
  runs: BuildDto[],
): Map<string, Record<BuildStatus, number>> => {
  const countsByDate = new Map<string, Record<BuildStatus, number>>();
  runs.forEach((run) => {
    const timestamp = run.finishedAt ?? run.startedAt ?? run.createdAt;
    const dayKey = timestamp.toISOString().slice(0, 10);
    const bucket = countsByDate.get(dayKey) ?? {
      queued: 0,
      running: 0,
      completed: 0,
      failed: 0,
    };
    bucket[run.status] += 1;
    countsByDate.set(dayKey, bucket);
  });
  return countsByDate;
};

export const resolveBuildTimestamp = (build: BuildDto): Date =>
  build.finishedAt ?? build.startedAt ?? build.createdAt;

const resolveBuildDurationSeconds = (build: BuildDto): number | null => {
  if (!(build.startedAt instanceof Date) || !(build.finishedAt instanceof Date)) {
    return null;
  }

  return Math.max((build.finishedAt.getTime() - build.startedAt.getTime()) / 1000, 0);
};

export const buildJobCounts = (
  jobs: JobListItemDto[] | undefined,
): Partial<Record<BuildStatus, number>> =>
  (jobs ?? []).reduce<Partial<Record<BuildStatus, number>>>(
    (acc, job) => {
      acc[job.status] = (acc[job.status] ?? 0) + 1;
      return acc;
    },
    {
      queued: 0,
      running: 0,
      completed: 0,
      failed: 0,
    },
  );

export const buildCountedJobFilterOptions = ({
  jobs = [],
  pipelineJobs,
  stageJobs,
  jobNameJobs,
  tagJobs,
  pipelineOptions,
  stageOptions,
  jobOptions,
  tagOptions,
}: {
  jobs?: readonly JobListItemDto[];
  pipelineJobs?: readonly JobListItemDto[];
  stageJobs?: readonly JobListItemDto[];
  jobNameJobs?: readonly JobListItemDto[];
  tagJobs?: readonly JobListItemDto[];
  pipelineOptions: readonly TableDropdownOption[];
  stageOptions: readonly TableDropdownOption[];
  jobOptions: readonly TableDropdownOption[];
  tagOptions: readonly MetricsToolbarTagOption[];
}): {
  pipelineOptions: TableDropdownOption[];
  stageOptions: TableDropdownOption[];
  jobOptions: TableDropdownOption[];
  tagOptions: MetricsToolbarTagOption[];
} => ({
  pipelineOptions: buildCountedDropdownOptions(
    pipelineOptions,
    buildStringCountMap(pipelineJobs ?? jobs, (job) => job.pipelineId),
  ),
  stageOptions: buildCountedDropdownOptions(
    stageOptions,
    buildStringCountMap(stageJobs ?? jobs, (job) => job.stageName),
  ),
  jobOptions: buildCountedDropdownOptions(
    jobOptions,
    buildStringCountMap(jobNameJobs ?? jobs, (job) => job.jobName),
  ),
  tagOptions: buildCountedDropdownOptions(
    normaliseMetricsToolbarTagOptions(tagOptions),
    buildStringArrayCountMap(tagJobs ?? jobs, (job) => job.tags),
  ),
});

export const buildJobRunMetricSummary = (
  runs: BuildDto[],
  chartDates: string[],
): JobRunMetricsSummary => {
  const buckets = buildJobRunMetricBuckets(runs);

  const orderedDates = resolveChartDates(chartDates, buckets.keys());
  const { passRateChartData, frequencyChartData, durationChartData, flakeScoreChartData } =
    buildMetricChartSeriesFromBuckets(buckets, orderedDates);
  const summary = buildMetricSummaryFromBuckets(buckets, orderedDates);

  return {
    passRateChartData,
    frequencyChartData,
    durationChartData,
    flakeScoreChartData,
    passRate: summary.passRate,
    frequencyPerDay: summary.frequencyPerDay,
    avgDurationSeconds: summary.avgDurationSeconds,
    flakeScore: summary.flakeScore,
  };
};

export const buildJobRunMetricBuckets = (runs: BuildDto[]): Map<string, JobRunMetricBucket> => {
  const buckets = new Map<string, JobRunMetricBucket>();

  [...runs]
    .sort((left, right) => {
      const timestampDelta =
        resolveBuildTimestamp(left).getTime() - resolveBuildTimestamp(right).getTime();
      return timestampDelta === 0 ? left.id.localeCompare(right.id) : timestampDelta;
    })
    .forEach((run) => {
      const timestamp = resolveBuildTimestamp(run);
      const dayKey = timestamp.toISOString().slice(0, 10);
      const bucket = buckets.get(dayKey) ?? createDailyExecutionMetricBucket();
      bucket.total += 1;
      if (run.status === 'completed') {
        bucket.completed += 1;
      }
      if (run.status === 'failed') {
        bucket.failed += 1;
      }
      bucket.flakeOutcomes.push(run.status === 'completed' ? 'passed' : 'notPassed');
      const durationSeconds = resolveBuildDurationSeconds(run);
      if (typeof durationSeconds === 'number') {
        bucket.durationSumSeconds += durationSeconds;
        bucket.durationCount += 1;
      }
      buckets.set(dayKey, bucket);
    });

  return buckets;
};

export const buildFallbackJobRuns = (jobs: JobListItemDto[]): BuildDto[] =>
  jobs.flatMap((job) =>
    (job.runSummaries ?? []).map((summary) => {
      const durationMs =
        job.latestRun?.id === summary.id
          ? (job.latestRun.durationMs ?? null)
          : (job.metrics?.averageDurationMs ?? null);
      const finishedAt = summary.timestamp ?? null;
      const startedAt =
        finishedAt instanceof Date && typeof durationMs === 'number'
          ? new Date(finishedAt.getTime() - durationMs)
          : null;
      const createdAt = finishedAt ?? job.lastUpdatedAt ?? new Date(0);

      return {
        id: summary.id,
        projectId: job.projectId,
        pipelineId: job.pipelineId ?? null,
        pipelineName: job.pipelineName ?? null,
        organisationId: job.organisationId,
        createdById: '',
        buildKey: summary.buildKey ?? null,
        jobName: job.jobName,
        stageName: job.stageName ?? null,
        pipelineUrl: job.pipelineUrl ?? null,
        jobUrl: job.jobUrl ?? null,
        lifecycleState: summary.lifecycleState ?? null,
        conclusion: summary.conclusion ?? null,
        providerStatus: summary.providerStatus ?? null,
        providerConclusion: summary.providerConclusion ?? null,
        requiresManualAction: summary.requiresManualAction ?? null,
        status: summary.status,
        createdAt,
        startedAt,
        finishedAt,
        tags: job.tags ?? [],
        isLatest: job.latestRun?.id === summary.id,
        deletedAt: job.deletedAt,
      } satisfies BuildDto;
    }),
  );

export const filterJobsBySearch = (
  jobs: JobListItemDto[],
  searchValue: string,
): JobListItemDto[] => {
  const normalizedSearch = searchValue.trim().toLowerCase();
  if (normalizedSearch.length === 0) {
    return jobs;
  }

  return jobs.filter((job) =>
    [
      job.pipelineName ?? '',
      job.stageName ?? '',
      job.jobName,
      job.status,
      job.latestRunBuildKey ?? '',
      ...(job.tags ?? []),
    ].some((value) => value.toLowerCase().includes(normalizedSearch)),
  );
};

export const filterJobRunsBySearch = (runs: BuildDto[], searchValue: string): BuildDto[] => {
  const normalizedSearch = searchValue.trim().toLowerCase();
  if (normalizedSearch.length === 0) {
    return runs;
  }

  return runs.filter((run) =>
    [
      run.pipelineName ?? '',
      run.stageName ?? '',
      run.jobName ?? '',
      run.buildKey ?? '',
      run.status,
      ...(run.tags ?? []),
    ].some((value) => value.toLowerCase().includes(normalizedSearch)),
  );
};

export const buildJobMetricCards = ({
  options,
  counts,
  deltas,
  jobTotal,
  runTotal,
  previousCounts,
  previousJobTotal,
  previousRunTotal,
  loading,
  showDelta,
}: {
  options: Array<MetricCardOption<BuildStatus>>;
  counts: Partial<Record<BuildStatus, number>>;
  deltas?: Partial<Record<BuildStatus, number>>;
  jobTotal: number;
  runTotal: number;
  previousCounts?: Partial<Record<BuildStatus, number>>;
  previousJobTotal?: number;
  previousRunTotal?: number;
  loading: boolean;
  showDelta: boolean;
}): MetricCardItem[] => {
  const cards = buildStatusMetricCards({
    options,
    counts,
    deltas,
    total: jobTotal,
    previousCounts,
    previousTotal: previousJobTotal,
    loading,
    showDelta,
  });
  const [totalCard, ...statusCards] = cards;
  const runDelta =
    showDelta && previousRunTotal !== undefined ? runTotal - previousRunTotal : undefined;
  const hasNoRunData = runTotal === 0 && (previousRunTotal ?? 0) === 0;
  const runPercentageDelta =
    showDelta && previousRunTotal !== undefined && previousRunTotal > 0
      ? Math.round(((runTotal - previousRunTotal) / previousRunTotal) * 100 * 100) / 100
      : undefined;
  let resolvedRunDelta: string | number | undefined;
  if (hasNoRunData) {
    resolvedRunDelta = undefined;
  } else if (runDelta === undefined || runDelta === 0) {
    resolvedRunDelta = showDelta ? '-' : undefined;
  } else {
    resolvedRunDelta = runDelta > 0 ? `+${runDelta}` : runDelta;
  }
  const runDeltaTone =
    resolvedRunDelta === undefined || resolvedRunDelta === '-'
      ? 'neutral'
      : (runDelta ?? 0) > 0
        ? 'positive'
        : 'negative';
  const resolvedRunPercentageDelta =
    runPercentageDelta === undefined || runPercentageDelta === 0
      ? undefined
      : runPercentageDelta > 0
        ? `+${runPercentageDelta}%`
        : `${runPercentageDelta}%`;
  const runPercentageDeltaTone =
    resolvedRunPercentageDelta === undefined
      ? 'neutral'
      : (runPercentageDelta ?? 0) > 0
        ? 'positive'
        : 'negative';

  return [
    {
      ...totalCard,
      status: 'Job Runs',
      count: runTotal.toLocaleString(),
      secondaryValue: formatRelatedEntityCountLabel(jobTotal, 'Job'),
      delta: resolvedRunDelta,
      deltaTone: runDeltaTone,
      countDelta: resolvedRunDelta,
      countDeltaTone: runDeltaTone,
      percentage: undefined,
      percentageDelta: resolvedRunPercentageDelta,
      percentageDeltaTone: runPercentageDeltaTone,
    },
    ...statusCards,
  ];
};

export const sortHistoryRows = (
  rows: EntityDailyHistoryRow[],
  sortKey: DailyHistorySortKey,
  sortDirection: 'asc' | 'desc',
): EntityDailyHistoryRow[] =>
  [...rows].sort((left, right) => {
    const resolveSortValue = (row: EntityDailyHistoryRow): string => {
      if (sortKey === 'pipelineLabel') {
        return row.prefixParts?.[0]?.label ?? '';
      }
      if (sortKey === 'stageLabel') {
        return row.prefixParts?.[1]?.label ?? '';
      }
      if (sortKey === 'prefixLabel') {
        return row.prefixLabel ?? '';
      }
      return row.name;
    };
    const leftValue = resolveSortValue(left);
    const rightValue = resolveSortValue(right);
    const result = leftValue.localeCompare(rightValue, undefined, { sensitivity: 'base' });
    if (result !== 0) {
      return sortDirection === 'asc' ? result : -result;
    }
    const leftPipeline = left.prefixParts?.[0]?.label ?? '';
    const rightPipeline = right.prefixParts?.[0]?.label ?? '';
    const pipelineComparison = leftPipeline.localeCompare(rightPipeline, undefined, {
      sensitivity: 'base',
    });
    if (pipelineComparison !== 0) {
      return sortDirection === 'asc' ? pipelineComparison : -pipelineComparison;
    }
    const leftStage = left.prefixParts?.[1]?.label ?? '';
    const rightStage = right.prefixParts?.[1]?.label ?? '';
    const stageComparison = leftStage.localeCompare(rightStage, undefined, {
      sensitivity: 'base',
    });
    if (stageComparison !== 0) {
      return sortDirection === 'asc' ? stageComparison : -stageComparison;
    }
    const fallbackResult = left.name.localeCompare(right.name, undefined, {
      sensitivity: 'base',
    });
    return sortDirection === 'asc' ? fallbackResult : -fallbackResult;
  });
